import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { parseISO } from 'date-fns';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useGetClinicSettingsGeneralQuery, ClinicOfficeHoursType } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Text } from '@televet/kibble-ui/build/components/Text';

interface IClinicOfficeHoursState {
  clinicIsOpen: boolean;
  bannerIsDismissed: boolean;
}

const AfterHoursNoticeBanner = (): JSX.Element | null => {
  const { currentClinicId } = useClinicUser();

  const [clinicOfficeHourState, setClinicOfficeHourState] = useState<IClinicOfficeHoursState>({
    clinicIsOpen: true,
    bannerIsDismissed: false,
  });

  const { data: currentClinicData, loading } = useGetClinicSettingsGeneralQuery({
    variables: { id: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !currentClinicId,
  });

  /**
   * Determine if a clinic is open via clinic.clinicOfficeHours
   */
  const getIsClinicOpen = useCallback((): boolean => {
    // If for some reason there are not office hours set up, still allow messaging to work
    if (loading) {
      return true;
    } else {
      if (!currentClinicData) return false;

      const now = new Date();
      const todaysOfficeHours = currentClinicData.findUniqueClinic?.clinicOfficeHours
        ?.filter(({ type }) => type === ClinicOfficeHoursType.BusinessHours)
        .find((oo) => oo.dayOfWeek === now.getDay() && oo.isEnabled);

      if (!todaysOfficeHours) return false;

      if (todaysOfficeHours.isOpenTwentyFourHours) return true;

      // Parse dates and extract the time
      const parsedOpenAt = parseISO(todaysOfficeHours.openAt);
      const parsedCloseAt = parseISO(todaysOfficeHours.closeAt);

      const openAtToday = new Date(now.getTime());
      openAtToday.setHours(parsedOpenAt.getHours(), parsedOpenAt.getMinutes(), parsedOpenAt.getSeconds(), 0);

      const closeAtToday = new Date(now.getTime());
      closeAtToday.setHours(parsedCloseAt.getHours(), parsedCloseAt.getMinutes(), parsedCloseAt.getSeconds(), 0);

      // Check if we are between, otherwise the clinic is closed
      return now.getTime() >= openAtToday.getTime() && now.getTime() <= closeAtToday.getTime();
    }
  }, [currentClinicData, loading]);

  /**
   * Check if the clinic is open every 5 minutes
   * This does not do anything if settings are changed in flow.
   * That would require a refresh
   */
  useEffect(() => {
    const checkIsClinicOpen = (): void => {
      if (clinicOfficeHourState.bannerIsDismissed) return;
      const isOpen = getIsClinicOpen();
      setClinicOfficeHourState((state: IClinicOfficeHoursState) => {
        if (state.clinicIsOpen === isOpen) return state;
        return {
          ...state,
          clinicIsOpen: isOpen,
        };
      });
    };

    // Fire the first time
    if (clinicOfficeHourState.clinicIsOpen) {
      checkIsClinicOpen();
    }
    // Fire every 5 minutes
    const interval = setInterval(checkIsClinicOpen, 50000);

    return (): void => clearInterval(interval);
  }, [clinicOfficeHourState, getIsClinicOpen]);

  const onBannerClosedClicked = (): void => {
    if (currentClinicId) {
      sessionStorage.setItem(`oooBannerDismissed-${currentClinicId}`, 'true');
    }

    setClinicOfficeHourState((state) => {
      return {
        ...state,
        bannerIsDismissed: true,
      };
    });
  };

  useEffect(() => {
    let oooBannerDismissed: string | null = null;

    if (currentClinicId) {
      oooBannerDismissed = sessionStorage.getItem(`oooBannerDismissed-${currentClinicId}`);
    }

    if (oooBannerDismissed) {
      setClinicOfficeHourState((state) => {
        return {
          ...state,
          bannerIsDismissed: true,
        };
      });
    }
  }, [currentClinicId]);

  const outOfOfficeEnabled = useMemo(() => {
    return !!currentClinicData?.findUniqueClinic?.clinicSetting?.outOfOfficeEnabled;
  }, [currentClinicData]);

  if (clinicOfficeHourState.clinicIsOpen || clinicOfficeHourState.bannerIsDismissed || !outOfOfficeEnabled) return null;

  return (
    <Alert
      status="info"
      title={
        <Text size="sm" fontWeight="bold">
          Out of office message visible to client
        </Text>
      }
      mx={1}
      mt={1}
      onClose={onBannerClosedClicked}
    />
  );
};

export default AfterHoursNoticeBanner;
